import { useState } from 'react'
import type { DragEvent, KeyboardEvent, ReactNode } from 'react'
import { Rechenabbruch, api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { Hinweisfenster, Texteingabe } from './Nachfrage'
import { useDarf } from './Rechte'
import { Zeilenmenue } from './Zeilenmenue'
import type { Menuepunkt } from './Zeilenmenue'
import { satz, t } from './sprache'
import type { Belegordner } from './typen'

/**
 * **Ein Ordnerbaum, zwei Seiten** (2.16 Abschnitt 5.5, R980–R985).
 *
 * Der Befund vom 2026-10-03: *„Die Ordner sollen vollständig vom Anwender
 * verwaltbar sein, genauso wie ein Ordner im Windows Explorer."* Bis dahin gab
 * es im Buchungsjournal drei Knöpfe unter dem Baum — und eine Taste F2, die
 * auf dem Knopf stand und nie etwas tat —, in den Datenimporte nur die Wahl.
 *
 * Dieses Bauteil zeichnet die Ordner **einer** Gesellschaft: je Geschäftsjahr
 * einen Jahresordner, darunter die eigenen. Es bringt alles mit, was den Baum
 * verwaltet — Rechtsklick, Tasten, Ziehen, die Fragen und die Antwort, wenn
 * etwas nicht geht. Die Seite, die es einbettet, sagt nur, was gewählt ist,
 * welche Zahl neben dem Ordner steht und was mit gezogenen Belegen geschieht.
 */

/* -------------------------------------------------------------------------
   Die zwei Ordnersymbole
   -------------------------------------------------------------------------
   Dieselbe Sprache wie die Bereichssymbole (7.2, R151): viewBox 0 0 24 24,
   Strichstärke 1,5, keine Füllung, Farbe geerbt. Sie tragen **nie** eine
   Zustandsfarbe — die gehört den gefüllten Zustandssymbolen, und die beiden
   Sprachen dürfen sich nicht ähneln.

   **Zwei Zeichnungen, nicht eine mit Farbe:** Welcher Ordner offen ist, ist
   die wichtigste Auskunft dieses Bildschirms, und sie darf nicht allein an
   einem Farbton hängen (R141). Der offene Ordner ist aufgeklappt gezeichnet;
   wer die Farbe nicht sieht, sieht die Form. */

export function Ordnersymbol({ offen }: { offen: boolean }) {
  return (
    <svg
      className="ordnersymbol"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {offen ? (
        <>
          <path d="M3 8V6.5A1.5 1.5 0 0 1 4.5 5h4.2l1.8 2h6A1.5 1.5 0 0 1 18 8.5V10" />
          <path d="M3.2 19h13.3a1.5 1.5 0 0 0 1.45-1.1L20.3 11H6.4a1.5 1.5 0 0 0-1.45 1.1L3.2 19Z" />
        </>
      ) : (
        <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4.2l1.8 2h9A1.5 1.5 0 0 1 21 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-11Z" />
      )}
    </svg>
  )
}

/** Was gezogen wird — Belege oder ein Ordner, immer einer Gesellschaft. */
const BELEGE = 'application/x-coko-belege'
const ORDNER = 'application/x-coko-ordner'

/** Ziehbare Belege — die Zeile einer Liste setzt das beim Anfassen. */
export function belegeZiehen(e: DragEvent, einheit: string, belege: { id: number; periode: number }[]) {
  e.dataTransfer.setData(BELEGE, JSON.stringify({ einheit, ids: belege.map((b) => b.id), perioden: belege.map((b) => b.periode) }))
  e.dataTransfer.effectAllowed = 'move'
}

/** Ob ein Ordner Belege dieser Perioden aufnimmt — nur aus seinem Jahr (R985). */
export function nimmtAuf(o: Belegordner, perioden: number[]): boolean {
  return perioden.every((p) => o.jahrVon == null || o.jahrBis == null || (p >= o.jahrVon && p <= o.jahrBis))
}

/** Der Jahresordner, in dem ein Beleg ohne eigenen Ordner steht (R984). */
export function jahresordnerVon(ordner: Belegordner[], periode: number): Belegordner | null {
  return ordner.find((o) => o.istEingang && nimmtAuf(o, [periode])) ?? null
}

/**
 * Die Antwort des Rechenkerns als Satz — mit dem Weg, wenn er einen nennt.
 *
 * `null` bei einer Störung: Sie steht im Störungsfenster, und ein zweites
 * Fenster darüber sagte dasselbe noch einmal (7.2 Abschnitt 30, R1003/R1004).
 */
function fehlersatz(e: unknown): { meldung: string; weg?: string } | null {
  if (e instanceof Rechenabbruch) {
    return { meldung: satz(e.meldung), weg: e.handreichung ? satz(e.handreichung) : undefined }
  }
  const befund = fachlicherSatz(e)
  return befund ? { meldung: befund } : null
}

/** Belege, die der einbettenden Seite ausgeschnitten vorliegen (Strg+X auf einer Zeile). */
export type Belegablage = { perioden: number[]; einfuegen: (ziel: number) => Promise<void> }

export function Ordnerbaum({
  einheit,
  ordner,
  gewaehlt,
  aufWahl,
  zahl,
  versatz = 0,
  aufGeaendert,
  belegablage,
  aufBelegeGezogen,
}: {
  einheit: string
  ordner: Belegordner[]
  gewaehlt: number | null
  aufWahl: (id: number) => void
  /** Die Zahl neben dem Ordner — Belege, Datenimporte oder Treffer. */
  zahl: (o: Belegordner) => ReactNode
  /** Wie viele Ebenen über dem Baum stehen — in den Datenimporten die Gesellschaft. */
  versatz?: number
  /** Nach jeder Änderung: neu laden. */
  aufGeaendert: () => void
  belegablage?: Belegablage | null
  /** Belege wurden auf einen Ordner gezogen (R983). */
  aufBelegeGezogen: (ids: number[], ziel: number) => Promise<void>
}) {
  const [menue, setMenue] = useState<{ x: number; y: number; ordner: Belegordner } | null>(null)
  const [frage, setFrage] = useState<{ art: 'neu' | 'umbenennen'; ordner: Belegordner } | null>(null)
  const [fehler, setFehler] = useState<{ meldung: string; weg?: string } | null>(null)
  const [ausgeschnitten, setAusgeschnitten] = useState<Belegordner | null>(null)
  const [ziel, setZiel] = useState<number | null>(null)
  // Was der Angemeldete nicht darf, bietet der Baum gar nicht an — weder im
  // Menü noch auf einer Taste noch beim Ziehen (R1180).
  const darf = useDarf()
  const darfAnlegen = darf('ordnerAnlegen')
  const darfUmbenennen = darf('ordnerUmbenennen')
  const darfVerschieben = darf('ordnerVerschieben')
  const darfLoeschen = darf('ordnerLoeschen')

  async function tue(arbeit: () => Promise<unknown>) {
    try {
      await arbeit()
    } catch (e) {
      setFehler(fehlersatz(e))
    } finally {
      aufGeaendert()
    }
  }

  const loeschen = (o: Belegordner) => void tue(() => api.ordnerLoeschen(o.id))

  /** Was Strg+V an dieser Stelle einfügt — ein ausgeschnittener Ordner oder Belege. */
  function einfuegbar(o: Belegordner): (() => Promise<unknown>) | null {
    if (darfVerschieben && ausgeschnitten && ausgeschnitten.id !== o.id && ausgeschnitten.jahrVon === o.jahrVon) {
      const was = ausgeschnitten
      return async () => {
        await api.ordnerVerschieben(was.id, o.id)
        setAusgeschnitten(null)
      }
    }
    if (belegablage && nimmtAuf(o, belegablage.perioden)) {
      return () => belegablage.einfuegen(o.id)
    }
    return null
  }

  function punkte(o: Belegordner): Menuepunkt[] {
    const eigen = !o.istEingang
    const einfuegen = einfuegbar(o)
    return [
      { schluessel: 'ordner.menue.neu', wenn: darfAnlegen, tun: () => setFrage({ art: 'neu', ordner: o }) },
      { schluessel: 'ordner.menue.umbenennen', wenn: eigen && darfUmbenennen, tun: () => setFrage({ art: 'umbenennen', ordner: o }) },
      { art: 'trenner' },
      { schluessel: 'ordner.menue.ausschneiden', wenn: eigen && darfVerschieben, tun: () => setAusgeschnitten(o) },
      { schluessel: 'ordner.menue.einfuegen', wenn: einfuegen !== null, tun: () => einfuegen && void tue(einfuegen) },
      { art: 'trenner' },
      { schluessel: 'ordner.menue.loeschen', wenn: eigen && darfLoeschen, tun: () => loeschen(o) },
    ]
  }

  /*
    **Die Tasten des Explorers, solange der Baum den Fokus hat.** Sie halten
    an, bevor sie das Fenster erreichen: Dort horcht die Belegliste auf Entf
    und Einfg, und ein Entf auf einem Ordner darf keinen Beleg löschen.
  */
  function beiTaste(e: KeyboardEvent, o: Belegordner) {
    const strg = e.ctrlKey || e.metaKey
    let tun: (() => void) | null = null

    if (e.key === 'F2' && !o.istEingang && darfUmbenennen) {
      tun = () => setFrage({ art: 'umbenennen', ordner: o })
    } else if (e.key === 'Delete' && !o.istEingang && darfLoeschen) {
      // Auch ein voller Ordner: Die Antwort sagt, warum es nicht geht (R981).
      tun = () => loeschen(o)
    } else if (e.key === 'Insert' && darfAnlegen) {
      tun = () => setFrage({ art: 'neu', ordner: o })
    } else if (strg && e.key.toLowerCase() === 'x' && !o.istEingang && darfVerschieben) {
      tun = () => setAusgeschnitten(o)
    } else if (strg && e.key.toLowerCase() === 'v') {
      const einfuegen = einfuegbar(o)
      tun = einfuegen ? () => void tue(einfuegen) : null
    }

    if (tun) {
      e.preventDefault()
      e.stopPropagation()
      tun()
    }
  }

  /*
    **Ziehen verschiebt** (R983) — auf einen Ordner derselben Gesellschaft und
    desselben Jahres. Was nicht passt, nimmt der Ordner gar nicht erst an: Der
    Zeiger sagt es, bevor die Maus losgelassen ist.
  */
  function passtHin(e: DragEvent, o: Belegordner): boolean {
    return e.dataTransfer.types.includes(BELEGE) || (darfVerschieben && e.dataTransfer.types.includes(ORDNER))
      ? o.einheit === einheit
      : false
  }

  async function abgelegt(e: DragEvent, o: Belegordner) {
    e.preventDefault()
    setZiel(null)
    const belege = e.dataTransfer.getData(BELEGE)
    const ordnerdaten = e.dataTransfer.getData(ORDNER)

    if (belege) {
      const was = JSON.parse(belege) as { einheit: string; ids: number[]; perioden: number[] }
      if (was.einheit !== einheit) return
      await tue(() => aufBelegeGezogen(was.ids, o.id))
    } else if (ordnerdaten && darfVerschieben) {
      const was = JSON.parse(ordnerdaten) as { einheit: string; id: number }
      if (was.einheit !== einheit || was.id === o.id) return
      await tue(() => api.ordnerVerschieben(was.id, o.id))
    }
  }

  function zweig(eltern: number | null, tiefe: number): ReactNode[] {
    return ordner
      .filter((o) => o.eltern === eltern)
      .flatMap((o) => [
        <li key={o.id}>
          <button
            type="button"
            className={[
              'ordnereintrag',
              o.id === gewaehlt ? 'gewaehlt' : '',
              ausgeschnitten?.id === o.id ? 'ausgeschnitten' : '',
              ziel === o.id ? 'ablageziel' : '',
            ].filter(Boolean).join(' ')}
            style={{ paddingLeft: `${8 + (tiefe + versatz) * 16}px` }}
            aria-current={o.id === gewaehlt ? 'true' : undefined}
            draggable={!o.istEingang && darfVerschieben}
            onDragStart={(e) => {
              e.dataTransfer.setData(ORDNER, JSON.stringify({ einheit, id: o.id }))
              e.dataTransfer.effectAllowed = 'move'
            }}
            onDragOver={(e) => {
              if (passtHin(e, o)) {
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                setZiel(o.id)
              }
            }}
            onDragLeave={() => setZiel((z) => (z === o.id ? null : z))}
            onDrop={(e) => void abgelegt(e, o)}
            onClick={() => aufWahl(o.id)}
            onKeyDown={(e) => beiTaste(e, o)}
            onContextMenu={(e) => {
              e.preventDefault()
              aufWahl(o.id)
              // Ein Menü ohne einen erlaubten Punkt geht gar nicht erst auf (R1180).
              if (punkte(o).some((p) => p.art !== 'trenner' && p.wenn !== false)) {
                setMenue({ x: e.clientX, y: e.clientY, ordner: o })
              }
            }}
          >
            {Array.from({ length: tiefe + versatz }, (_, i) => (
              <span key={i} className="einrueckung" aria-hidden="true" />
            ))}
            <Ordnersymbol offen={o.id === gewaehlt} />
            <span className="name">{o.name}</span>
            <span className="nummer">{zahl(o)}</span>
          </button>
        </li>,
        ...zweig(o.id, tiefe + 1),
      ])
  }

  const fenster = menue !== null || frage !== null || fehler !== null

  return (
    <>
      {zweig(null, 0)}

      {/* Der Baum steht in einer Liste; Menü und Fenster hängen an einem eigenen Eintrag. */}
      {fenster && (
        <li className="ordnerbaumfenster" role="none">
      {menue && (
        <Zeilenmenue x={menue.x} y={menue.y} punkte={punkte(menue.ordner)} aufSchliessen={() => setMenue(null)} />
      )}

      {/* Das Programm fragt selbst (R394). */}
      {frage?.art === 'neu' && (
        <Texteingabe
          titel={t('ordner.neu')}
          beschriftung={t('ordner.neu.name')}
          platzhalter={t('ordner.neu.platzhalter')}
          bestaetigen={t('ordner.neu.anlegen')}
          aufSchliessen={() => setFrage(null)}
          aufEingabe={(name) => {
            const unter = frage.ordner
            setFrage(null)
            void tue(() => api.ordnerAnlegen(einheit, name, unter.id))
          }}
        />
      )}

      {frage?.art === 'umbenennen' && (
        <Texteingabe
          titel={t('ordner.umbenennen')}
          beschriftung={t('ordner.neu.name')}
          anfangswert={frage.ordner.name}
          bestaetigen={t('ordner.umbenennen.tun')}
          aufSchliessen={() => setFrage(null)}
          aufEingabe={(name) => {
            const o = frage.ordner
            setFrage(null)
            void tue(() => api.ordnerUmbenennen(o.id, name))
          }}
        />
      )}

      {/*
        **Was nicht geht, sagt ein Fenster** — mit dem Grund und, wo es einen
        gibt, dem Weg. Ein Satz, der unter dem Baum aufblitzt, wird übersehen.
      */}
      {fehler && (
        <Hinweisfenster
          titel={t('ordner.fehler.titel')}
          meldung={fehler.meldung}
          weg={fehler.weg}
          aufSchliessen={() => setFehler(null)}
        />
      )}
        </li>
      )}
    </>
  )
}
